import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Icon } from '@codex-brightworld/icons';
import { Button } from '@codex-brightworld/basics';
import { formatPrice } from '@codex-brightworld/helpers';
import { EnvironmentThemeProvider } from '@codex-brightworld/themes';
import { AddToBasketErrorMessage } from '../../../AddToBasketErrorMessage/AddToBasketErrorMessage';
import MoreDetails from './MoreDetails';
import MoreDetailsMobile from './MoreDetailsMobile';

export const TYPE_INSTALLATION = 'installation';
export const TYPE_RECYCLING = 'recycling';

const SingleContainerWrapper = styled.div`
  @font-face {
    font-family: 'CurrysSans';
    src: url('https://currys-ssl.cdn.dixons.com/css/themes/fonts/currys/CurrysSans-Regular.woff') format('woff'),
        url('https://currys-ssl.cdn.dixons.com/css/themes/fonts/currys/CurrysSans-Regular.otf') format('truetype'),
        url('https://currys-ssl.cdn.dixons.com/css/themes/fonts/currys/CurrysSans-Regular.eot?') format('embedded-opentype');
  }
  color: white;
  position: relative;
  padding: 24px 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  border-bottom: ${(props) => (
    props.hasBorder ? `1px solid ${props.theme.colors.shades[5]}` : 'none'
  )};
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    padding: 10px 0;
  }
`;

const StyledIcon = styled(Icon)`
  width: 28px;
  height: 28px;
  color: ${({ theme }) => theme.colors.link.default};
  margin-right: 10px;
  stroke: none;
`;

const StyledChevron = styled(Icon)`
  margin-right: 20px;
  width: 24px;
  height: 24px;
  stroke: none;

  svg path:last-child {
    stroke: none;
    fill: ${(props) => props.theme.colors.primary.purple};
  }

  &:hover {
    cursor: pointer;
  }

  @media ${(props) => props.theme.breakpoints.mobile} {
    position: relative;
    left: 9px;
    margin-left: auto;
    margin-right: 10px;
  }
`;

const StyledText = styled.div`
  font-family: CurrysSans;
  font-size: 16px;
  width: 144px;
  margin-right: auto;
  color: ${({ theme }) => theme.colors.shades[1]};

  @media ${(props) => props.theme.breakpoints.mobile} {
    font-size: 13px;
    margin-right: 0;
    width: 140px;
  }
`;

const StyledButton = styled(Button)`
  font-family: CurrysSans;
  width: 125px;
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    width: 100%;
    margin-left: 0;
    order: 2;
  }
`;

const ButtonIcon = styled(Icon)`
  width: 20px;
  height: 20px;
  margin: -5px 10px -5px 0px;
`;

const InProgressButton = styled(Button)`
  background: #fff;
  font-family: CurrysSans;
  height: 44px;
  width: 110px;
  margin-left: auto;

  ${ButtonIcon} {
    margin: -5px 0;
  }

  @media ${(props) => props.theme.breakpoints.mobile} {
    width: 100%;
    margin-left: 0;
    order: 2;
  }
`;

const SingleContainer = ({
  type,
  typeText,
  productPrice,
  buttonText,
  productCurrency,
  hasBorder,
  addOnClick,
  moreDetails,
  moreInfoText,
  isPopoverDisplayed,
  handlePopoverDisplay,
  failedToUpdateBasket,
  pleaseTryAgain,
  displayError,
  isInProgress
}) => {
  const { html, tooltipHtml, isExpanded } = moreDetails;
  const [isToggled, setToggled] = useState(isExpanded);

  const toggle = () => {
    handlePopoverDisplay(false);
    setToggled(!isToggled);
  };

  const recycleInstallationIcon = typeText === 'Installation' ? 'home_instalation' : 'recycling_2';

  return (
    <EnvironmentThemeProvider>
      <SingleContainerWrapper hasBorder={hasBorder}>
        {displayError && (
        <AddToBasketErrorMessage
          failedToUpdateBasket={failedToUpdateBasket}
          pleaseTryAgain={pleaseTryAgain}
          isFullWidth
        />
        )}
        <StyledIcon icon={recycleInstallationIcon} />
        <StyledText>
          {typeText}
          {' '}
          -
          {' '}
          {formatPrice(productPrice, true, productCurrency)}
        </StyledText>
        <StyledChevron
          icon={'arrowright'}
          rotate={isToggled ? 270 : 90}
          onClick={() => toggle()}
        />
        <MoreDetailsMobile
          tooltipHtml={tooltipHtml}
          handlePopoverDisplay={handlePopoverDisplay}
          isPopoverDisplayed={isPopoverDisplayed}
          moreInfoText={moreInfoText}
          displayError={displayError}
        />
        {!isInProgress && (
          <StyledButton positive onClick={addOnClick}>
            {buttonText}
          </StyledButton>
        )}
        {isInProgress && (
          <InProgressButton disabled>
            <ButtonIcon icon={'loading'} spin />
          </InProgressButton>
        )}
        <MoreDetails
          tooltipHtml={tooltipHtml}
          html={html}
          type={type}
          isToggled={isToggled}
          isPopoverDisplayed={isPopoverDisplayed}
          moreInfoText={moreInfoText}
          handlePopoverDisplay={handlePopoverDisplay}
          displayError={displayError}
        />
      </SingleContainerWrapper>
    </EnvironmentThemeProvider>
  );
};

SingleContainer.propTypes = {
  type: PropTypes.string.isRequired,
  productId: PropTypes.number.isRequired,
  typeText: PropTypes.string.isRequired,
  buttonText: PropTypes.string.isRequired,
  productPrice: PropTypes.number.isRequired,
  productCurrency: PropTypes.string,
  addOnClick: PropTypes.func.isRequired,
  hasBorder: PropTypes.bool,
  moreDetails: PropTypes.shape({
    isExpanded: PropTypes.bool.isRequired,
    html: PropTypes.string.isRequired,
    tooltipHtml: PropTypes.string.isRequired
  }).isRequired,
  failedToUpdateBasket: PropTypes.string.isRequired,
  pleaseTryAgain: PropTypes.string.isRequired,
  displayError: PropTypes.bool,
  isInProgress: PropTypes.bool,
  moreInfoText: PropTypes.string.isRequired,
  isPopoverDisplayed: PropTypes.bool.isRequired,
  handlePopoverDisplay: PropTypes.func.isRequired
};

SingleContainer.defaultProps = {
  productCurrency: 'GBP',
  hasBorder: false,
  displayError: false,
  isInProgress: false
};

export default SingleContainer;
